<script setup>
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import CurrencyInput from '@/components/CurrencyInput.vue'
  import { ref, onMounted } from 'vue'
  import { useRouter, RouterLink, RouterView } from 'vue-router'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useContractStore } from '@/stores/contractStore'
  import { useRegionStore } from '@/stores/regionStore'
  import { useUserStore } from '@/stores/userStore'
  import { convertToHalfWidthNumber, convertToFullWidth } from '@/components/logics/Util'

  const router = useRouter()
  const dialogStore = useDialogStore()
  const contractStore = useContractStore()
  const regionStore = useRegionStore()
  const userStore = useUserStore()

  const breadcrumbs = [{ name: 'ダッシュボード', url: 'dashboard' }, { name: '契約確認' }]

  // 入力ハンドラ /////////////////////////////////////////

  // 全角文字入力の処理
  const handleFullWidthInput = (event, key) => {
    contractStore.formData[key] = convertToFullWidth(event.target.value)
  }

  // 電話番号入力時の処理
  const handlePhoneInput = (event, index) => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    contractStore.formData.phone[index] = halfWidthValue
  }

  // 郵便番号入力時の処理
  const handlePostalCodeInput = async (event, index) => {
    // 全角を半角に変換
    const value = event.target.value
    const halfWidthValue = convertToHalfWidthNumber(value)
    contractStore.formData.postalCode[index] = halfWidthValue

    // 両方の入力が揃っているか確認
    if (contractStore.isPostalCodeFilled()) {
      contractStore.setAddress(await regionStore.getAddressWithPostalCode(contractStore.postalCodeWithHyphen))
    }
  }

  // 法人番号入力時の処理
  const handleCorporateNumberInput = event => {
    // 全角を半角に変換
    const halfWidthValue = convertToHalfWidthNumber(event.target.value)
    contractStore.formData.corporateNumber = halfWidthValue
  }

  // 都道府県が変更されたときのハンドラー
  const handlePrefectureChange = () => {
    contractStore.setPrefectureKana(regionStore)
  }

  // methods /////////////////////////////////////////
  onMounted(async () => {
    // ユーザー情報を取得
    await userStore.getCurrentUser()

    // 権限チェック（管理者権限がない場合はダッシュボードへリダイレクト）
    if (!userStore.isAdmin()) {
      await dialogStore.showAlertDialog('権限エラー', 'この機能にアクセスする権限がありません。')
      router.push('/dashboard')
      return
    }

    // 契約情報を取得
    await contractStore.getCurrentContract()

    // 都道府県データを取得
    await regionStore.getPrefectures()
  })

  // 保存ボタンクリック時の処理
  const onSave = async () => {
    // ストアを使って契約情報を更新
    const message = await contractStore.updateContract()
    if (message) {
      await dialogStore.showMessageDialog('ディーモ', message)
    }
  }

  // 有料プラン契約ボタンクリック時の処理
  const onUpgradeToPaidPlan = async () => {
    // プラン選択画面へ遷移
    router.push('/subscription')
  }

  // 解約ボタンクリック時の処理
  const onCancelPlan = async () => {
    // 解約画面へ遷移
    router.push('/cancellation')
  }

  // プラン変更ボタンクリック時の処理
  const onChangePlan = async () => {
    // プラン変更画面への遷移
    router.push('/subscription')
  }

  // 契約変更キャンセルボタンクリック時の処理
  const onCancelContractChange = async () => {
    // 確認ダイアログを表示
    const confirmed = await dialogStore.showConfirmDialog('契約変更のキャンセル', '予定されている契約変更をキャンセルしますか？')

    if (confirmed) {
      // ストアを使って契約変更をキャンセル
      const message = await contractStore.cancelContractChange()
      if (message) {
        await dialogStore.showMessageDialog('ディーモ', message)
      }
    }
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <h2>契約確認</h2>
      <button type="button" class="save-btn" @click="onSave">保存</button>
    </div>
    <div class="main-body">
      <div class="setting-content">
        <dl class="scroller" v-if="contractStore.formData">
          <dt>契約設定</dt>
          <dd>
            <dl>
              <dt>法人番号</dt>
              <dd><input type="text" class="corporate-number" v-model="contractStore.formData.corporateNumber" placeholder="1234567890123" maxlength="13" @input="handleCorporateNumberInput($event)" /></dd>
              <dt>法人・団体名</dt>
              <dd><input id="company-name" type="text" class="wide" v-model="contractStore.formData.name" placeholder="法人・団体名を全角で入力してください" v-autokana:company-name @input="handleFullWidthInput($event, 'name')" /></dd>
              <dt>法人・団体名カナ</dt>
              <dd><input id="company-name-kana" type="text" class="wide" v-model="contractStore.formData.nameKana" placeholder="法人・団体名を全角カタカナで入力してください" @input="handleFullWidthInput($event, 'nameKana')" /></dd>
              <dt>電話番号</dt>
              <dd>
                <div class="phone-grid">
                  <input type="text" v-model="contractStore.formData.phone[0]" maxlength="5" placeholder="012" @input="handlePhoneInput($event, 0)" />
                  <span class="hyphen">-</span>
                  <input type="text" v-model="contractStore.formData.phone[1]" maxlength="4" placeholder="3456" @input="handlePhoneInput($event, 1)" />
                  <span class="hyphen">-</span>
                  <input type="text" v-model="contractStore.formData.phone[2]" maxlength="4" placeholder="7890" @input="handlePhoneInput($event, 2)" />
                </div>
              </dd>
              <dt>郵便番号</dt>
              <dd>
                <div class="postal-code-grid">
                  <input type="text" v-model="contractStore.formData.postalCode[0]" maxlength="3" placeholder="123" @input="handlePostalCodeInput($event, 0)" />
                  <span class="hyphen">-</span>
                  <input type="text" v-model="contractStore.formData.postalCode[1]" maxlength="4" placeholder="4567" @input="handlePostalCodeInput($event, 1)" />
                </div>
              </dd>
              <dt>都道府県</dt>
              <dd>
                <div class="select prefecture-select">
                  <select v-model="contractStore.formData.prefecture" @change="handlePrefectureChange">
                    <option value="">選択してください</option>
                    <option v-for="pref in regionStore.prefectureNames" :key="pref" :value="pref">
                      {{ pref }}
                    </option>
                  </select>
                </div>
              </dd>
              <dt>住所1</dt>
              <dd><input id="address1" type="text" class="wide" v-model="contractStore.formData.address1" placeholder="全角で入力してください" v-autokana:address1 @input="handleFullWidthInput($event, 'address1')" /></dd>
              <dt>住所1カナ</dt>
              <dd><input id="address1-kana" type="text" class="wide" v-model="contractStore.formData.address1Kana" placeholder="全角カタカナで入力してください" @input="handleFullWidthInput($event, 'address1Kana')" /></dd>
              <dt>住所2</dt>
              <dd><input id="address2" type="text" class="wide" v-model="contractStore.formData.address2" placeholder="全角で入力してください" v-autokana:address2 @input="handleFullWidthInput($event, 'address2')" /></dd>
              <dt>住所2カナ</dt>
              <dd><input id="address2-kana" type="text" class="wide" v-model="contractStore.formData.address2Kana" placeholder="全角カタカナで入力してください" @input="handleFullWidthInput($event, 'address2Kana')" /></dd>
            </dl>
          </dd>
          <div class="border"></div>
          <!-- 契約状況セクション -->
          <dt>契約状況</dt>
          <dd>
            <dl>
              <!-- 有料プランの場合の有料プラン申込日 -->
              <template v-if="contractStore.isPaidStatus">
                <dt>有料プラン申込日</dt>
                <dd>
                  <div class="value-label">{{ contractStore.paidPlanStartDateFormatted }}</div>
                </dd>
              </template>

              <dt>現在の契約プラン</dt>
              <dd>
                <div class="value-label">
                  <span>{{ contractStore.contractStatusDisplay }}</span>
                </div>
              </dd>

              <!-- 次の契約プランが現在のプランと異なる場合 -->
              <template v-if="contractStore.shouldShowNextContractPlan">
                <dt>更新後の契約プラン</dt>
                <dd>
                  <div class="value-label">
                    <span>{{ contractStore.nextContractStatusDisplay }}</span>
                  </div>
                </dd>
              </template>

              <!-- 有料プランの場合の次の契約更新日 -->
              <template v-if="contractStore.isPaidStatus">
                <!-- <dt>現在の契約期間開始日</dt>
                <dd>
                  <div class="value-label">{{ contractStore.currentPeriodStartDateFormatted }}</div>
                </dd> -->
                <dt>次の契約更新日</dt>
                <dd>
                  <div class="value-label">{{ contractStore.currentPeriodEndDateFormatted }}</div>
                </dd>
              </template>

              <!-- トライアルの場合 -->
              <template v-if="contractStore.isTrialStatus">
                <dt>トライアル終了日</dt>
                <dd>
                  <div class="value-label">{{ contractStore.trialEndDateFormatted }}</div>
                </dd>
              </template>

              <!-- 有料プランの場合のボタン表示（rootユーザーのみ） -->
              <template v-if="contractStore.isPaidStatus && userStore.isRoot()">
                <dt></dt>
                <dd>
                  <!-- 変更がスケジュールされていない場合はプラン変更・解約ボタンを表示 -->
                  <div v-if="contractStore.shouldShowPlanActions" class="plan-actions">
                    <button class="btn btn-secondary" @click="onChangePlan">プラン変更</button>
                    <button class="btn btn-danger" @click="onCancelPlan">解約</button>
                  </div>
                  <!-- 変更がスケジュールされている場合は変更キャンセルボタンを表示 -->
                  <div v-if="contractStore.shouldShowCancelChangeButton" class="plan-actions">
                    <button class="btn btn-warning" @click="onCancelContractChange">契約変更をキャンセル</button>
                  </div>
                </dd>
              </template>

              <!-- 解約済みの場合 -->
              <template v-if="contractStore.isCanceledStatus">
                <dt>解約日</dt>
                <dd>
                  <div class="value-label">{{ contractStore.canceledAtFormatted }}</div>
                </dd>
              </template>

              <!-- 無料または有料でない場合のボタン（rootユーザーのみ） -->
              <template v-if="contractStore.shouldShowUpgradeButton && userStore.isRoot()">
                <dt></dt>
                <dd>
                  <button class="btn btn-primary" @click="onUpgradeToPaidPlan">有料プランを契約</button>
                  <span class="payment-note">請求書支払いのみ</span>
                </dd>
              </template>
            </dl>
          </dd>
        </dl>
      </div>
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />
</template>

<style lang="less" scoped>
  .wide {
    width: 100% !important;
    max-width: 500px !important;
  }

  .corporate-number {
    width: 160px !important;
    max-width: 160px !important;
  }

  .prefecture-select {
    width: 160px !important;
    max-width: 160px !important;
  }

  .phone-grid {
    display: grid;
    grid-template-columns: 60px auto 60px auto 60px;
    gap: 8px;
    align-items: center;
    justify-content: flex-start;

    input {
      width: 60px !important;
      max-width: 60px !important;
    }
  }
  .postal-code-grid {
    display: grid;
    grid-template-columns: 60px auto 60px;
    gap: 8px;
    align-items: center;
    justify-content: flex-start;

    input {
      width: 60px !important;
      max-width: 60px !important;
    }
  }

  .payment-note {
    margin-left: 10px;
    color: #6c757d;
    font-size: 12px;
  }

  .cancel-note {
    margin-left: 10px;
    color: #6c757d;
    font-size: 12px;
  }

  .billing-note {
    margin-left: 10px;
    color: #6c757d;
    font-size: 12px;
  }

  .btn {
    background-color: var(--active-color);
    color: #fff;
    border-radius: 20px;
    padding: 5px 15px;
    letter-spacing: 0.05em;
    font-size: 12px;
    transition: opacity 0.3s;
    white-space: nowrap;
    border: none;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }

    &:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }

    &.btn-primary {
      background-color: #007bff;
      border-radius: 4px;
      padding: 10px 20px;
      font-size: 14px;

      &:hover {
        background-color: #0056b3;
        opacity: 1;
      }
    }

    &.btn-secondary {
      background-color: #6c757d;
      border-radius: 4px;
      padding: 10px 20px;
      font-size: 14px;

      &:hover {
        background-color: #5a6268;
        opacity: 1;
      }
    }

    &.btn-danger {
      background-color: #dc3545;
      border-radius: 4px;
      padding: 10px 20px;
      font-size: 14px;

      &:hover {
        background-color: #c82333;
        opacity: 1;
      }
    }

    &.btn-warning {
      background-color: #ffc107;
      color: #212529;
      border-radius: 4px;
      padding: 10px 20px;
      font-size: 14px;

      &:hover {
        background-color: #e0a800;
        opacity: 1;
      }
    }
  }

  .plan-actions {
    margin-top: 10px;
    display: flex;
    gap: 10px;
    align-items: center;
  }

  .notice-text {
    font-size: 12px;
    color: #6c757d;
  }

  .border {
    border-top: 1px solid #e9ecef;
    margin: 20px 0;
  }

  .save-btn {
    background-color: #1a7247;
    color: #f7f7f7;
    border: none;
    border-radius: 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 20px;
    height: 30px;
    font-size: 12px;
    font-weight: bold;
    transition: opacity 0.3s;
    white-space: nowrap;
    cursor: pointer;

    &:hover {
      opacity: 0.7;
    }

    &:disabled {
      background-color: #6c757d;
      cursor: not-allowed;
    }
  }

  .main-header {
    display: flex;
    align-items: center;

    h2 {
      flex-grow: 1;
      margin: 0;
    }
  }

  /* 契約状況の表示項目の高さ調整 */
  .value-label {
    margin-top: 5px;
  }
</style>
